import { useCallback } from "react";
import { useApiWithHandling } from "../hooks/useApiWithHandling";
import {
  MiniPackGenerationDocumentRequest,
  MiniPackGenerationSourceScope,
} from "./miniPackGenerationService";

export interface MiniPackReportSectionConfigurationDto {
  templateSectionId: string;
  sectionKey: string;
  sectionName: string;
  sortOrder: number;
  documents: MiniPackGenerationDocumentRequest[];
}

export interface MiniPackReportConfigurationDto {
  sourceScope: MiniPackGenerationSourceScope;
  tailNumber?: string;
  assemblyComponentId?: string;
  subAssemblyId?: string;
  sections: MiniPackReportSectionConfigurationDto[];
  hasSavedConfiguration: boolean;
}

export interface SaveMiniPackReportConfigurationRequest {
  sourceScope: MiniPackGenerationSourceScope;
  tailNumber?: string;
  assemblyComponentId?: string;
  subAssemblyId?: string;
  sections: MiniPackReportSectionConfigurationDto[];
}

export const useMiniPackReportService = () => {
  const { get, put } = useApiWithHandling();

  const getConfiguration = useCallback(async (params: {
    sourceScope: MiniPackGenerationSourceScope;
    assemblyComponentId?: string;
    subAssemblyId?: string;
    tailNumber?: string;
  }) => {
    const query = new URLSearchParams({ sourceScope: params.sourceScope });
    if (params.assemblyComponentId) {
      query.set("assemblyComponentId", params.assemblyComponentId);
    }
    if (params.subAssemblyId) {
      query.set("subAssemblyId", params.subAssemblyId);
    }
    if (params.tailNumber) {
      query.set("tailNumber", params.tailNumber);
    }

    const response = await get<MiniPackReportConfigurationDto>(
      `api/mini-pack-reports/configuration?${query.toString()}`,
      {
        showLoader: false,
        showErrorSnackbar: false,
        throwOnError: true,
      }
    );

    return response?.data as MiniPackReportConfigurationDto;
  }, [get]);

  const saveConfiguration = useCallback(async (request: SaveMiniPackReportConfigurationRequest) => {
    const response = await put<MiniPackReportConfigurationDto>(
      "api/mini-pack-reports/configuration",
      request,
      {
        showLoader: false,
        showErrorSnackbar: true,
        throwOnError: true,
      }
    );

    return response?.data as MiniPackReportConfigurationDto;
  }, [put]);

  return {
    getConfiguration,
    saveConfiguration,
  };
};
